import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Stack,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useConfirm } from 'material-ui-confirm';

import {
  adminCreateCoursePricingTemplate,
  adminUpdateCoursePricingTemplate,
  ICoursePricingTemplate,
  TAdminCreateCoursePricingTemplate,
  TAdminUpdateCoursePricingTemplate,
  TCoursePricingTemplateSubscriptionPricingBillingPeriodTypes,
} from '../../../../../../store/V2/CoursePricingTemplatesSlice';
import { capitalizeFirstLetter } from '../../../../../../helper';
import { TPTextField } from '../../../../../../components/Inputs';
import DarkLoadingButton, {
  DarkButton,
  defaultButtonStyles,
} from '../../../../../../components/V2/Buttons';
import AddOneTimePricingModal from './AddOneTimePricingModal';
import AddSubscriptionPricingModal from './AddSubscriptionPricingModal';

interface AddCoursePricingTemplateModalProps {
  open: boolean;
  onClose: () => void;
  coursePricingTemplate?: ICoursePricingTemplate;
  readOnly?: boolean;
  duplicate?: boolean;
}

export type TOneTimePricingOption = {
  currency: string;
  amount: number;
  country?: string;
};

export type TSubscriptionPricingOption = {
  currency: string;
  amount: number;
  country?: string;
  billingPeriod: {
    count: number;
    type: TCoursePricingTemplateSubscriptionPricingBillingPeriodTypes;
  };
};

type TState = {
  name: string;
  isCreditChecked: boolean;
  isOneTimeChecked: boolean;
  oneTimePricings: TOneTimePricingOption[];
  isSubscriptionChecked: boolean;
  subscriptionPricings: TSubscriptionPricingOption[];
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialState: TState = {
  name: '',
  isCreditChecked: false,
  isOneTimeChecked: false,
  oneTimePricings: [],
  isSubscriptionChecked: false,
  subscriptionPricings: [],
};

const AddCoursePricingTemplateModal: FunctionComponent<AddCoursePricingTemplateModalProps> = ({
  open,
  onClose,
  coursePricingTemplate,
  readOnly = false,
  duplicate = false,
}) => {
  const confirm = useConfirm();
  const dispatch = useDispatch();

  // State
  const [isLoading, setLoading] = useState(false);
  const [state, setState] = useState({ ...initialState });
  const [
    isAddNewOneTimePricingModalOpen,
    setAddNewOneTimePricingModalOpen,
  ] = useState(false);
  const [
    isAddNewSubscriptionPricingModalOpen,
    setAddNewSubscriptionPricingModalOpen,
  ] = useState(false);

  let mode: 'new' | 'edit' | 'view' | 'duplicate' = 'new';
  let modalTitle = 'Add New Course Pricing Template';

  if (coursePricingTemplate) {
    mode = 'edit';
    modalTitle = 'Edit Course Pricing Template';

    if (readOnly) {
      mode = 'view';
      modalTitle = 'Course Pricing Template Details';
    } else if (duplicate) {
      mode = 'duplicate';
      modalTitle = 'Add New Course Pricing Template';
    }
  }

  const isSubmitDisabled =
    !state.name.trim().length ||
    (state.isOneTimeChecked && state.oneTimePricings.length < 1) ||
    (state.isSubscriptionChecked && state.subscriptionPricings.length < 1);

  useEffect(() => {
    if (mode !== 'new' && coursePricingTemplate) {
      setState((prevState) => {
        const newStateData = { ...prevState };

        if (coursePricingTemplate.name) {
          newStateData.name = coursePricingTemplate.name;
        }

        if (coursePricingTemplate.credit) {
          newStateData.isCreditChecked = coursePricingTemplate.credit;
        }

        if (coursePricingTemplate.oneTime) {
          newStateData.isOneTimeChecked = coursePricingTemplate.oneTime;

          if (
            coursePricingTemplate.oneTimePricings &&
            coursePricingTemplate.oneTimePricings.length
          ) {
            newStateData.oneTimePricings =
              coursePricingTemplate.oneTimePricings || [];
          }
        }

        if (coursePricingTemplate.subscription) {
          newStateData.isSubscriptionChecked =
            coursePricingTemplate.subscription;

          if (
            coursePricingTemplate.subscriptionPricings &&
            coursePricingTemplate.subscriptionPricings.length
          ) {
            newStateData.subscriptionPricings =
              coursePricingTemplate.subscriptionPricings || [];
          }
        }

        return newStateData;
      });
    }
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const addOneTimePricing = (option: TOneTimePricingOption) => {
    setState((prevState) => {
      if (
        !option.country &&
        prevState.oneTimePricings.some((item) => !item.country)
      ) {
        confirm({
          hideCancelButton: true,
          title: null,
          description: 'Pricing without the country is already exist',
        });

        return { ...prevState };
      }

      if (
        option.country &&
        prevState.oneTimePricings.some(
          (item) => item.country === option.country
        )
      ) {
        confirm({
          hideCancelButton: true,
          title: null,
          description: 'Pricing with the same country already exist',
        });

        return { ...prevState };
      }

      return {
        ...prevState,
        oneTimePricings: [...prevState.oneTimePricings, option],
      };
    });
  };

  const removeOneTimePricing = (option: TOneTimePricingOption) => {
    setState((prevState) => ({
      ...prevState,
      oneTimePricings: [...prevState.oneTimePricings].filter(
        (item) => item !== option
      ),
    }));
  };

  const addSubscriptionPricing = (option: TSubscriptionPricingOption) => {
    setState((prevState) => {
      if (
        !option.country &&
        prevState.subscriptionPricings.some((item) => !item.country)
      ) {
        confirm({
          hideCancelButton: true,
          title: null,
          description: 'Pricing without the country is already exist',
        });

        return { ...prevState };
      }

      if (
        option.country &&
        prevState.subscriptionPricings.some(
          (item) => item.country === option.country
        )
      ) {
        confirm({
          hideCancelButton: true,
          title: null,
          description: 'Pricing with the same country already exist',
        });

        return { ...prevState };
      }

      return {
        ...prevState,
        subscriptionPricings: [...prevState.subscriptionPricings, option],
      };
    });
  };

  const removeSubscriptionPricing = (option: TSubscriptionPricingOption) => {
    setState((prevState) => ({
      ...prevState,
      subscriptionPricings: [...prevState.subscriptionPricings].filter(
        (item) => item !== option
      ),
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (mode === 'new' || mode === 'duplicate') {
      const apiData: TAdminCreateCoursePricingTemplate = {
        name: state.name,
        credit: state.isCreditChecked,
        oneTime: state.isOneTimeChecked,
        subscription: state.isSubscriptionChecked,
      };

      if (state.isOneTimeChecked) {
        apiData.oneTimePricings = state.oneTimePricings || [];
      }

      if (state.isSubscriptionChecked) {
        apiData.subscriptionPricings = state.subscriptionPricings || [];
      }

      const res: any = await dispatch(
        adminCreateCoursePricingTemplate(apiData)
      );

      if (res.payload) {
        onClose();
      }
    } else if (mode === 'edit' && coursePricingTemplate) {
      const apiData: TAdminUpdateCoursePricingTemplate = {
        id: coursePricingTemplate._id,
        name: state.name,
        credit: state.isCreditChecked,
        oneTime: state.isOneTimeChecked,
        subscription: state.isSubscriptionChecked,
      };

      if (state.isOneTimeChecked) {
        apiData.oneTimePricings = state.oneTimePricings || [];
      }

      if (state.isSubscriptionChecked) {
        apiData.subscriptionPricings = state.subscriptionPricings || [];
      }

      const res: any = await dispatch(
        adminUpdateCoursePricingTemplate(apiData)
      );

      if (res.payload) {
        onClose();
      }
    }

    setLoading(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        {modalTitle}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <div style={{ paddingTop: '6px' }}>
          <Grid container spacing="20px">
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  {...textFieldProps}
                  label="Name *"
                  placeholder="Enter here..."
                  value={state.name}
                  InputProps={{ readOnly }}
                  onChange={(event) =>
                    updateStateAttributes({ name: event.target.value })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormGroup>
                <Stack>
                  <FormControlLabel
                    label="Credit"
                    control={
                      <Checkbox
                        disabled={readOnly}
                        checked={state.isCreditChecked}
                        onChange={(event, value) =>
                          updateStateAttributes({ isCreditChecked: value })
                        }
                      />
                    }
                  />
                  <FormControlLabel
                    label="One Time"
                    control={
                      <Checkbox
                        disabled={readOnly}
                        checked={state.isOneTimeChecked}
                        onChange={(event, value) =>
                          updateStateAttributes({
                            isOneTimeChecked: value,
                          })
                        }
                      />
                    }
                  />
                  {state.isOneTimeChecked ? (
                    <Collapse in>
                      <div>
                        {mode !== 'view' ? (
                          <Stack direction="row" justifyContent="flex-end">
                            <DarkButton
                              variant="contained"
                              color="inherit"
                              sx={{ ...defaultButtonStyles }}
                              onClick={() =>
                                setAddNewOneTimePricingModalOpen(true)
                              }
                            >
                              Add New Pricing
                            </DarkButton>
                          </Stack>
                        ) : null}
                        <div style={{ margin: '20px 0' }}>
                          <Typography fontSize="14px" fontWeight={600}>
                            Pricings *
                          </Typography>
                        </div>
                        <Stack gap="20px">
                          {state.oneTimePricings.map((item, index) => {
                            let _text = `${item.currency} ${item.amount}`;

                            if (item.country) {
                              _text += ` - ${item.country}`;
                            }

                            return (
                              <Stack
                                key={`one-time-pricing-item-${index}`}
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <Typography fontSize="14px" fontWeight={500}>
                                  {_text}
                                </Typography>
                                {mode !== 'view' ? (
                                  <IconButton
                                    onClick={() => removeOneTimePricing(item)}
                                  >
                                    <DeleteIcon sx={{ color: '#1E293B' }} />
                                  </IconButton>
                                ) : null}
                              </Stack>
                            );
                          })}
                        </Stack>
                        {!state.oneTimePricings.length ? (
                          <div>
                            <Typography fontSize="14px" fontWeight={500}>
                              No pricings are available
                            </Typography>
                          </div>
                        ) : null}
                      </div>
                    </Collapse>
                  ) : null}
                  <FormControlLabel
                    label="Subscription"
                    sx={{ mt: state.isOneTimeChecked ? '20px' : undefined }}
                    control={
                      <Checkbox
                        disabled={readOnly}
                        checked={state.isSubscriptionChecked}
                        onChange={(event, value) =>
                          updateStateAttributes({
                            isSubscriptionChecked: value,
                          })
                        }
                      />
                    }
                  />
                  {state.isSubscriptionChecked ? (
                    <Collapse in>
                      <div>
                        {mode !== 'view' ? (
                          <Stack direction="row" justifyContent="flex-end">
                            <DarkButton
                              variant="contained"
                              color="inherit"
                              sx={{ ...defaultButtonStyles }}
                              onClick={() =>
                                setAddNewSubscriptionPricingModalOpen(true)
                              }
                            >
                              Add New Pricing
                            </DarkButton>
                          </Stack>
                        ) : null}
                      </div>
                      <div style={{ margin: '20px 0' }}>
                        <Typography fontSize="14px" fontWeight={600}>
                          Pricings *
                        </Typography>
                      </div>
                      <Stack gap="20px">
                        {state.subscriptionPricings.map((item, index) => {
                          let _text = `${item.currency} ${item.amount}`;

                          if (item.country) {
                            _text += ` - ${item.country}`;
                          }

                          _text += ` (Every ${
                            item.billingPeriod.count
                          } ${capitalizeFirstLetter(item.billingPeriod.type)})`;

                          return (
                            <Stack
                              key={`one-time-pricing-item-${index}`}
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Typography fontSize="14px" fontWeight={500}>
                                {_text}
                              </Typography>
                              {mode !== 'view' ? (
                                <IconButton
                                  onClick={() =>
                                    removeSubscriptionPricing(item)
                                  }
                                >
                                  <DeleteIcon sx={{ color: '#1E293B' }} />
                                </IconButton>
                              ) : null}
                            </Stack>
                          );
                        })}
                      </Stack>
                      {!state.subscriptionPricings.length ? (
                        <div>
                          <Typography fontSize="14px" fontWeight={500}>
                            No pricings are available
                          </Typography>
                        </div>
                      ) : null}
                    </Collapse>
                  ) : null}
                </Stack>
              </FormGroup>
            </Grid>
          </Grid>
        </div>
      </DialogContent>
      {mode !== 'view' ? (
        <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
          <DarkLoadingButton
            disableElevation
            variant="contained"
            color="inherit"
            loading={isLoading}
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            sx={{ ...defaultButtonStyles }}
          >
            {mode === 'edit' ? 'Save' : 'Add'}
          </DarkLoadingButton>
        </DialogActions>
      ) : null}
      {/* MODALS */}
      {isAddNewOneTimePricingModalOpen ? (
        <AddOneTimePricingModal
          open={isAddNewOneTimePricingModalOpen}
          onClose={() => setAddNewOneTimePricingModalOpen(false)}
          onAddPricing={addOneTimePricing}
        />
      ) : null}
      {isAddNewSubscriptionPricingModalOpen ? (
        <AddSubscriptionPricingModal
          open={isAddNewSubscriptionPricingModalOpen}
          onClose={() => setAddNewSubscriptionPricingModalOpen(false)}
          onAddPricing={addSubscriptionPricing}
        />
      ) : null}
    </Dialog>
  );
};

export default AddCoursePricingTemplateModal;
